import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Download, FileText, Image as ImageIcon, ArrowRight, Calendar, Newspaper, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const featuredAssets = [{
  title: "Official Media Kit",
  description: "Comprehensive guide including organization history, key statistics, and leadership bios.",
  format: "PDF",
  size: "2.4 MB",
  icon: <FileText className="size-6" />
}, {
  title: "Brand Assets & Logos",
  description: "High-resolution logos, color palettes, and usage guidelines for partners and media.",
  format: "ZIP",
  size: "15.8 MB",
  icon: <ImageIcon className="size-6" />
}, {
  title: "2024 Impact Report",
  description: "Detailed analysis of economic growth, policy wins, and community outreach metrics.",
  format: "PDF",
  size: "5.1 MB",
  icon: <Newspaper className="size-6" />
}];
const pressReleases = [{
  id: 1,
  title: "COHCC Releases 2024 State of Hispanic Business Report",
  date: "October 12, 2024",
  excerpt: "New data reveals Hispanic-owned businesses in Colorado now contribute over $40B to the state economy, marking a historic milestone in regional growth.",
  category: "Economic Data"
}, {
  id: 2,
  title: "Annual Gala Raises Record Funds for Leadership Programs",
  date: "September 05, 2024",
  excerpt: "The 46th Annual Gala brought together 1,200+ community leaders, securing vital funding for the next generation of Hispanic executives.",
  category: "Events"
}, {
  id: 3,
  title: "Chamber Advocates for Small Business Policy Changes",
  date: "July 15, 2024",
  excerpt: "COHCC leadership testified at the State Capitol today, urging lawmakers to reduce regulatory burdens for minority-owned startups.",
  category: "Policy & Advocacy"
}, {
  id: 4,
  title: "New Workforce Initiative Partners with Major Tech Firms",
  date: "June 22, 2024",
  excerpt: "A strategic partnership with top technology companies will provide digital skills training to over 500 local entrepreneurs this year.",
  category: "Workforce"
}];
export default function MediaNewsroom() {
  return <section id="media-newsroom" className="dark bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {}
        <div className="mb-16 md:mb-20 max-w-3xl">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-primary">
            Media & Newsroom
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Access official press materials, latest announcements, and economic insights from the Colorado Hispanic Chamber of Commerce.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {featuredAssets.map((asset, i) => <Card key={i} data-index={i} className="bg-card border-border/50 shadow-sm hover:shadow-md transition-all duration-300 group">
              <CardHeader className="pb-4">
                <div className="mb-4 p-3 bg-primary/10 w-fit rounded-lg text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {asset.icon}
                </div>
                <CardTitle className="text-xl font-semibold">{asset.title}</CardTitle>
                <CardDescription className="mt-2 text-muted-foreground line-clamp-2">
                  {asset.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <span className="bg-secondary/10 text-secondary-foreground px-2 py-1 rounded-sm">
                    {asset.format}
                  </span>
                  <span>{asset.size}</span>
                </div>
              </CardContent>
              <CardFooter className="pt-0">
                <Button variant="outline" className="w-full justify-between group-hover:border-primary/50">
                  Download Asset
                  <Download className="size-4 ml-2" />
                </Button>
              </CardFooter>
            </Card>)}
        </div>

        <Separator className="my-16 bg-border/60" />

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {}
          <div className="lg:col-span-4 space-y-8">
            <div>
              <h3 className="text-3xl font-bold mb-4">Latest Press Releases</h3>
              <p className="text-muted-foreground">
                Stay updated with the latest news, policy updates, and community stories from COHCC.
              </p>
            </div>
            
            <div className="p-6 rounded-xl bg-muted/30 border border-border/50">
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <Search className="size-4" /> Media Inquiries
              </h4>
              <p className="text-sm text-muted-foreground mb-6">
                For interview requests, official statements, or additional data, please contact our communications team.
              </p>
              <div className="space-y-1 text-sm">
                <p className="font-medium text-foreground">Communications Director</p>
                <p className="text-muted-foreground">media@cohcc.org</p>
                <p className="text-muted-foreground">+1 (303) 555-1234</p>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8 space-y-6">
            {pressReleases.map((release, i) => <div key={release.id} data-index={i} className="group flex flex-col md:flex-row gap-6 p-6 rounded-xl bg-card border border-border/40 hover:border-primary/30 hover:shadow-sm transition-all duration-300">
                <div className="md:w-1/4 flex flex-col gap-2 shrink-0">
                  <span className="text-sm font-semibold text-primary tracking-wide uppercase">
                    {release.category}
                  </span>
                  <div className="flex items-center text-muted-foreground text-sm">
                    <SafeHydrate><Calendar className="size-3.5 mr-2" /></SafeHydrate>
                    {release.date}
                  </div>
                </div>
                
                <div className="md:w-3/4 flex flex-col justify-between gap-4">
                  <div>
                    <h4 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">
                      {release.title}
                    </h4>
                    <p className="text-muted-foreground leading-relaxed">
                      {release.excerpt}
                    </p>
                  </div>
                  
                  <div className="flex justify-start">
                    <Link to="#" className="inline-flex items-center text-sm font-medium text-foreground hover:text-primary transition-colors">
                      Read Full Release <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </div>)}

            <div className="pt-8 flex justify-center md:justify-start">
              <Button variant="outline" size="lg" className="min-w-[200px]">
                View All News
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}